import { MobXProviderContext } from "mobx-react";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { getErrorTitle, WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES } from "../../../../constants/constants";
import { NO_IMAGE } from "../../../../assets";
import { ReferenceType } from "../../../../models/document";
import { ProviderAboutSection } from "../../../../models/manageWebSite";
import { normalizePlainText } from "../../../../utils/securityUtils";
import { appAccentPanelStyle, appDialogContentStyle, appDialogHeaderStyle, appDialogStyle, appFormSectionStyle, appMutedTextStyle, renderDialogFooter, renderSurfaceHeader } from "../../../../components/general/uiSurfaceStyles";

interface Props {
  data: ProviderAboutSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const splitLines = (value: string) => value.split("\n").map((item) => normalizePlainText(item, { maxLength: 220 })).filter(Boolean);

const EditModal: FC<Props> = ({ data, isOpen, toggleModal, flag, setFlag }) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [providerImageUrl, setProviderImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
      meetTheProvider: data?.meetTheProvider || "",
      carePhilosophy: data?.carePhilosophy || "",
      whyFamiliesChooseUs: (data?.whyFamiliesChooseUs || []).join("\n"),
      testimonials: (data?.testimonials || []).join("\n"),
    },
  });

  useEffect(() => {
    setProviderImageUrl(data?.providerSignedImg || null);
    setSelectedFile(null);
    reset({
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
      meetTheProvider: data?.meetTheProvider || "",
      carePhilosophy: data?.carePhilosophy || "",
      whyFamiliesChooseUs: (data?.whyFamiliesChooseUs || []).join("\n"),
      testimonials: (data?.testimonials || []).join("\n"),
    });
  }, [data, isOpen, reset]);

  const handleFileChange = (event: any) => {
    if (event.files && event.files[0]) {
      const file = event.files[0];
      setSelectedFile(file);
      setProviderImageUrl(URL.createObjectURL(file));
    }
  };

  const uploadProviderImage = async (objectKey: string) => {
    if (!selectedFile) {
      return;
    }

    const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(
      data?.homeId || "",
      ReferenceType.BUSINESS_HOME,
      objectKey,
    );
    await backendAPIStore.uploadFileToS3(presignedUrl, selectedFile);
  };

  const onSubmit = async (formData: any) => {
    try {
      setLoading(true);
      await backendAPIStore.init();
      const providerImageObjectKey = selectedFile ? `website/default_template/${data?.homeId}/draft/PROVIDER_ABOUT-photo` : data?.providerImage;
      if (selectedFile && providerImageObjectKey) {
        await uploadProviderImage(providerImageObjectKey);
      }
      await backendAPIStore.updateTemplateSection({
        ...data,
        enabled: !!formData.enabled,
        title: normalizePlainText(formData.title, { maxLength: 140 }),
        description: normalizePlainText(formData.description, { multiline: true, maxLength: 1200 }),
        providerImage: providerImageObjectKey,
        meetTheProvider: normalizePlainText(formData.meetTheProvider, { multiline: true, maxLength: 1200 }),
        carePhilosophy: normalizePlainText(formData.carePhilosophy, { multiline: true, maxLength: 1200 }),
        whyFamiliesChooseUs: splitLines(formData.whyFamiliesChooseUs),
        testimonials: splitLines(formData.testimonials),
        modifiedAt: new Date().toISOString(),
      });
      toggleModal(false);
      setFlag(!flag);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: "Unable to save the about page.", life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const chooseOptions = { icon: "pi pi-fw pi-images", className: "custom-choose-btn p-button-rounded p-button-outlined" };
  const cancelOptions = { icon: "pi pi-fw pi-times", className: "custom-cancel-btn p-button-rounded p-button-outlined p-button-danger" };

  const fileHeaderTemplate = (options: any) => {
    const { className, chooseButton, cancelButton } = options;

    return (
      <div className={className} style={{ backgroundColor: "transparent", display: "flex", alignItems: "center" }}>
        {chooseButton}
        {cancelButton}
      </div>
    );
  };

  return (
    <Dialog visible={isOpen} modal header={renderSurfaceHeader("About Page", "Manage provider story, philosophy, differentiators, testimonials, and provider photo.", "pi pi-users")} footer={renderDialogFooter({ errorMessage: null, loading, onCancel: () => toggleModal(false), onSubmit: handleSubmit(onSubmit), submitLabel: "Save About Page" })} style={appDialogStyle} headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle} onHide={() => toggleModal(false)}>
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#be185d" }}>About page</div>
        <div className="mt-2" style={appMutedTextStyle}>Share the human side of the home so families understand who is behind the care.</div>
      </div>
      <form>
        <div style={appFormSectionStyle}>
          <div className="flex align-items-center gap-2">
            <Controller name="enabled" control={control} render={({ field }) => <Checkbox inputId="aboutEnabled" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />} />
            <label htmlFor="aboutEnabled" style={{ fontWeight: 600 }}>Add About page into the website</label>
          </div>
        </div>
        <div style={{ ...appFormSectionStyle, marginTop: "1rem", textAlign: "center" }}>
          <label style={{ display: "block", marginBottom: "0.75rem" }}><strong>Provider Photo</strong></label>
          <img src={providerImageUrl || NO_IMAGE} width="200" height="200" style={{ objectFit: "cover", borderRadius: "20px", marginBottom: "1rem", border: "1px solid #f3d5e6" }} />
          <FileUpload
            mode="advanced"
            name="providerPhoto"
            onSelect={handleFileChange}
            accept="image/*"
            maxFileSize={WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES}
            headerTemplate={fileHeaderTemplate}
            chooseOptions={chooseOptions}
            cancelOptions={cancelOptions}
            emptyTemplate={<p className="m-0">Drag and drop provider photo here to upload.</p>}
          />
        </div>
        {[
          { name: "title", label: "Page Title", rows: 3 },
          { name: "description", label: "Description", rows: 4 },
          { name: "meetTheProvider", label: "Meet the Provider", rows: 5 },
          { name: "carePhilosophy", label: "Care Philosophy", rows: 5 },
          { name: "whyFamiliesChooseUs", label: "Why Families Choose Us", rows: 5 },
          { name: "testimonials", label: "Testimonials", rows: 5 },
        ].map(({ name, label, rows }) => (
          <div style={{ ...appFormSectionStyle, marginTop: "1rem" }} key={name}>
            <label htmlFor={name}><strong>{label}</strong></label>
            <Controller name={name as any} control={control} render={({ field }) => <InputTextarea id={name} {...field} rows={rows} style={{ width: "100%" }} />} />
          </div>
        ))}
      </form>
    </Dialog>
  );
};

export default EditModal;
